/* eslint-disable import/no-cycle */
import styled from '@emotion/styled';
import { Fonts, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import AquillaButton from 'aquilla/core/molecules/Button';
import Image from '@base/FutureImage';
import {
  commasIndianFormat,
  currencyCode,
  nFormatter
} from '@utils/textHelper';
import { segment } from '@actions/analytics';
import {
  CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
  CHECKOUT_SUBSCRIPTION_TYPE_SELECTED
} from '@cont/Checkout/constants/segment';
import { useEffect, useState } from 'react';
import useFetch from '@hooks/useFetch';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import usePurchaseDetails from '@cont/Checkout/usePurchaseDetails';
import {
  getIconicMetaInfo,
  getIconicUsersCount
} from '@cont/Checkout/apiHelper';
import isEmptyObject from '@utils/isEmptyObject';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import usePricings from '@cont/Checkout/usePricings';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import NormalFlowIconicCard from './IconicCard';
import PriceBreakDownTable from '../BaseComponents/PriceBreakDownTable';
import { NORMALFLOWSTEPS } from './NormalPlanSelection';
import {
  defaultSelectedPricing,
  discountedPricingIfAvailable
} from '../planSelectionHelper';

const AddIconicFlowPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
`;

const Title = styled(Typography)`
  font-weight: ${Fonts.SEMIBOLD};
  width: 280px;
`;

const AddIconicBottomInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_3B};
`;

const InfoCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
`;

const AddIconicInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
`;

const LearnerEnrolledDetails = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${Spacings.SPACING_2B};
  width: 100%;
  height: 42px;
  background-color: var(--color-i-blue-47);
  gap: ${Spacings.SPACING_1B};
  font-size: ${Fonts.H6};
  color: var(--color-i-blue);
  font-weight: ${Fonts.SEMIBOLD};
`;

const BottomCtaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
`;

const AddIconicFlowPage = ({
  setFlowSteps,
  preFilteredSubscriptionLists,
  purchaseDetails,
  hasIconicSubscription,
  setBundleAddOnData,
  setPreviousStep,
  discountedPricings,
  setCurrentEventProps,
  currentEventProps
}) => {
  const {
    subscription: {
      currency = '',
      price: plusSubscriptionPrice = {},
      referral = {},
      taxBreakdown = [],
      uid: planUID = ''
    } = {}
  } = purchaseDetails ?? {};

  const { query } = useRouter();
  const { price: iconicPrice = '', uid: iconicUID = '' } =
    hasIconicSubscription ?? {};
  const { goalUID = '' } = query;
  const [addIconic, setAddIconic] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [iconicMetaInfo, setIconicMetaInfo] = useState<
    Record<string, any> | undefined
  >();
  const [iconicUsersCount, setIconicUsersCount] = useState<
    Record<string, any> | undefined
  >();

  const { pricings } = usePricings({ goalUID });
  const { goal = {} } = pricings ?? {};
  const me = useMyInfo(getMe);

  const iconicSelectedEventProps = useCheckoutEventProps(goalUID);

  const referralCode = referral?.code;

  const { tax_breakdown = [] } = hasIconicSubscription ?? {};

  const { validateReferralInProgress, referralError: referralIconicError } =
    usePurchaseDetails({ subscriptionUid: iconicUID, referralCode });

  const { data: bundleAddOnsInfo, isLoading: fetchingBundleAddOns } = useFetch(
    isSubmitted
      ? `v2/subscriptions/bundle_add_ons/details/?subscription_uid=${
          addIconic ? iconicUID : planUID
        }${
          referral?.code
            ? `&referral=${encodeURIComponent(referral?.code)}`
            : ''
        }`
      : null,
    { useCamelCase: false },
    { shouldRetryOnError: false }
  );

  const isReferralAdded = !!referralCode;

  const eventProps = useCheckoutEventProps(
    goalUID,
    discountedPricingIfAvailable(
      addIconic ? hasIconicSubscription.uid : planUID,
      isReferralAdded,
      discountedPricings
    ) ||
      defaultSelectedPricing(
        addIconic ? hasIconicSubscription.uid : planUID,
        pricings
      ),
    false
  );

  const subscription_price =
    tax_breakdown.length > 0
      ? tax_breakdown.find((tax) => {
          return tax?.label === 'Total';
        })
      : null;

  const evtPayload = {
    ...eventProps,
    referral_code: referralCode || 'NA',
    is_referral_successful: Boolean(referralCode),
    subscription_price: Number(subscription_price?.amount)
  };

  useEffect(() => {
    setPreviousStep(NORMALFLOWSTEPS.PLANSELECTION);
    if (isSubmitted && !fetchingBundleAddOns) {
      if (bundleAddOnsInfo?.bundle_add_ons?.length) {
        setBundleAddOnData({
          bundleAddOnsInfo,
          skuDetails: addIconic
            ? hasIconicSubscription
            : purchaseDetails?.subscription
        });
        if (addIconic) setCurrentEventProps(evtPayload);
        setPreviousStep(NORMALFLOWSTEPS.ADDICONIC);
        setFlowSteps(NORMALFLOWSTEPS.ADDON);
      } else {
        segment.track(
          CHECKOUT_PROCEED_TO_PAYMENT_SELECTED,
          addIconic ? evtPayload : currentEventProps
        );
        pushRoute({
          pathname: '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
          query: {
            ...query,
            subscriptionUID: addIconic ? hasIconicSubscription.uid : planUID
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted, bundleAddOnsInfo, fetchingBundleAddOns]);

  const originalPrice = Math.round(iconicPrice - plusSubscriptionPrice);

  const referralCodeDiscountPrice = Math.round(
    (referral?.discountPercent * originalPrice) / 100
  );

  useEffect(() => {
    (async () => {
      const data = await getIconicMetaInfo(goalUID);
      if (!isEmptyObject(data) && !data?.error) setIconicMetaInfo(data);
    })();
  }, [goalUID]);

  useEffect(() => {
    (async () => {
      const data = await getIconicUsersCount();
      if (!isEmptyObject(data) && !data?.error) setIconicUsersCount(data);
    })();
  }, [goalUID]);

  const handleAddIconic = () => {
    segment.track(CHECKOUT_SUBSCRIPTION_TYPE_SELECTED, {
      ...iconicSelectedEventProps,
      subscription_type: 'ICONIC'
    });
    setAddIconic(true);
    setIsSubmitted(true);
  };

  const priceBreakDown =
    !validateReferralInProgress && !referralIconicError && referral?.code
      ? [
          [
            {
              name: 'Original price',
              price: `${currencyCode[currency]}${commasIndianFormat(
                originalPrice
              )}`,
              type: 'positive'
            },
            {
              name: `‘${referral?.code}’ code applied`,
              price: `-${currencyCode[currency]}${commasIndianFormat(
                referralCodeDiscountPrice
              )}`,
              type: 'negative'
            }
          ],
          [
            {
              name: 'Discounted price',
              price: `${currencyCode[currency]}${commasIndianFormat(
                originalPrice - referralCodeDiscountPrice
              )}`,
              type: 'positive'
            }
          ]
        ]
      : [
          [
            {
              name: 'Price',
              price: `${currencyCode[currency]}${commasIndianFormat(
                originalPrice
              )}`,
              type: 'positive'
            }
          ]
        ];

  return (
    <AddIconicFlowPageWrapper>
      <Title variant="h2">
        For the best prep, choose{' '}
        <span style={{ color: 'var(--color-i-orange)' }}>Iconic</span>
      </Title>
      <AddIconicInfoWrapper>
        <AddIconicBottomInfoWrapper>
          {iconicMetaInfo && (
            <InfoCardWrapper>
              {iconicMetaInfo?.appIconicData.map((iconicData) => {
                return (
                  <NormalFlowIconicCard
                    key={iconicData?.id}
                    iconicData={iconicData}
                  />
                );
              })}
            </InfoCardWrapper>
          )}
          <LearnerEnrolledDetails>
            Over {nFormatter(iconicUsersCount?.value, 1)?.toLowerCase()}{' '}
            learners prepare from Iconic
            <Image
              src="newPlanSelection/stars.svg"
              width={14}
              height={14}
              alt="Stars"
            />
          </LearnerEnrolledDetails>
          <PriceBreakDownTable priceBreakDown={priceBreakDown} />
        </AddIconicBottomInfoWrapper>
        <BottomCtaWrapper>
          <AquillaButton
            variant="primary"
            size="large"
            fullWidth
            onClick={handleAddIconic}
          >
            Add Iconic experience
          </AquillaButton>
          <AquillaButton
            variant="secondary"
            size="large"
            fullWidth
            onClick={() => {
              setAddIconic(false);
              setIsSubmitted(true);
            }}
          >
            Continue without Iconic
          </AquillaButton>
        </BottomCtaWrapper>
      </AddIconicInfoWrapper>
    </AddIconicFlowPageWrapper>
  );
};

export default AddIconicFlowPage;
